// Rep commissions (admins + managers). Pick a month → each rep's goods-subtotal RECEIVED that month ×
// their rate. Flow per rep: email the report → the rep approves it (or raises a question) → pay it
// (Airwallex or Mark paid). Paying is locked until the rep approves the current figure; an admin can
// override. Defaults to last month — the one you'd pay at the start of this month.
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import {
  fetchCommissions,
  setCommissionRate,
  setCommissionCurrency,
  settleCommission,
  unsettleCommission,
  fetchMyCommissions,
  syncBeneficiaries,
  payCommission,
  sendCommissionReport,
  resendPaidNotice,
  sendAllCommissionReports,
  approveMyCommission,
  queryMyCommission,
} from '../api/commissions.js';
import { fmtMoney } from '../lib/matrix.js';
import { useAuthStore } from '../stores/authStore.js';
import { can, CAP } from '../lib/access.js';

const MONTH_LABEL = (y, m) => `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} ${y}`;

const CURRENCIES = ['AUD', 'USD', 'NZD', 'GBP', 'EUR', 'CAD'];
const errText = (e, fallback) => e.response?.data?.error?.message || fallback;
const monthIdx = (y, m) => y * 12 + (m - 1);
const fmtWhen = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '');

// Where a rep's month is in report → approve → pay, in words (stage comes from the server).
const STAGE = {
  NOT_SENT: { label: 'Report not sent', cls: 'bg-surface-raised text-text-muted' },
  SENT: { label: 'Waiting for approval', cls: 'bg-accent-light text-text-primary' },
  QUERY: { label: 'Question from rep', cls: 'bg-error-light text-error' },
  CHANGED: { label: 'Changed since approval', cls: 'bg-warning-light text-warning' },
  APPROVED: { label: 'Approved by rep', cls: 'bg-success-light text-success' },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const prevMonth = () => {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  return m === 1 ? { year: y - 1, month: 12 } : { year: y, month: m - 1 };
};

function RepRow({ rep, period, onChanged, isAdmin, ended }) {
  const [rate, setRate] = useState(rep.rate ?? '');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => setRate(rep.rate ?? ''), [rep.rate]);
  const linked = !!rep.airwallexBeneficiaryId;
  const orders = rep.orders || [];
  const clawbacks = rep.clawbacks || [];
  const clawback = Number(rep.payout?.clawback || 0);
  const payoutCurrency = rep.payoutCurrency || 'AUD';
  const paidViaAirwallex = rep.settled && rep.payout?.method === 'airwallex';
  const report = rep.report || { stage: 'NOT_SENT' };
  const approved = report.stage === 'APPROVED';
  const who = rep.name || rep.email;

  // Paying before the rep approved: admins only, with a reason (recorded). null = cancelled.
  const overrideOpts = () => {
    if (approved) return {};
    const note = window.prompt(`${who} hasn't approved ${MONTH_LABEL(period.year, period.month)} yet.\n\nPay anyway? Add a short reason (it's recorded):`);
    if (note === null) return null;
    return { override: true, note: note.trim() || undefined };
  };

  const resendPaid = async () => {
    if (!window.confirm(`Email ${who} (${rep.email}) the "paid" confirmation for ${MONTH_LABEL(period.year, period.month)} again, with the remittance PDF?\n\nAdmins get a copy.`)) return;
    setBusy('paid');
    setMsg('');
    try {
      await resendPaidNotice(rep.repUserId, period.year, period.month);
      setMsg(`Paid confirmation emailed to ${rep.email}.`);
    } catch (e) {
      setMsg(errText(e, 'Could not send the email.'));
    } finally {
      setBusy('');
    }
  };

  const sendReport = async () => {
    const again = report.sentCount > 0;
    if (!window.confirm(`${again ? 'Resend' : 'Send'} the ${MONTH_LABEL(period.year, period.month)} commission report to ${who} (${rep.email})?\n\nAdmins get a copy.`)) return;
    setBusy('report');
    setMsg('');
    try {
      const r = await sendCommissionReport(rep.repUserId, period.year, period.month);
      setMsg(r.simulated ? 'Report recorded (email simulated in dev).' : `Report emailed to ${rep.email}.`);
      await onChanged();
    } catch (e) {
      setMsg(errText(e, 'Could not send the report.'));
    } finally {
      setBusy('');
    }
  };

  const changeCurrency = async (next) => {
    if (next === payoutCurrency) return;
    const needsReapproval = ['SENT', 'APPROVED', 'CHANGED', 'QUERY'].includes(report.stage);
    if (!window.confirm(`Pay ${who} in ${next} from now on?\n\nThis applies to all their unpaid months${needsReapproval ? `. ${who} will need to approve the new figure — resend the report after changing it` : ''}.`)) return;
    setBusy('cur');
    setMsg('');
    try {
      await setCommissionCurrency(rep.repUserId, next === 'AUD' ? null : next);
      await onChanged();
      setMsg(`Now paid in ${next}.`);
    } catch (e) {
      setMsg(errText(e, 'Could not change the currency.'));
    } finally {
      setBusy('');
    }
  };

  const saveRate = async () => {
    setBusy('rate');
    try {
      await setCommissionRate(rep.repUserId, rate === '' ? null : Number(rate));
      await onChanged();
    } finally {
      setBusy('');
    }
  };
  const settle = async () => {
    const opts = overrideOpts();
    if (opts === null) return;
    if (approved && !window.confirm(`Record ${fmtMoney(rep.payout?.commission || 0, payoutCurrency)} as paid to ${who}? Use this when the money was sent outside the platform.`)) return;
    setBusy('settle');
    setMsg('');
    try {
      await settleCommission(rep.repUserId, period.year, period.month, opts);
      await onChanged();
      setMsg(`Recorded as paid — ${who} has been emailed.`);
    } catch (e) {
      setMsg(errText(e, 'Could not record the payment.'));
    } finally {
      setBusy('');
    }
  };
  const unsettle = async () => {
    setBusy('settle');
    setMsg('');
    try {
      await unsettleCommission(rep.repUserId, period.year, period.month);
      await onChanged();
    } catch (e) {
      setMsg(e.response?.data?.error?.message || 'Could not undo.');
    } finally {
      setBusy('');
    }
  };

  const canSettle = rep.rate != null && rep.payout?.base > 0;

  // Pay via Airwallex — ONE transfer, in the rep's currency. Hard confirm (moves real money).
  const payVia = async () => {
    const amount = fmtMoney(rep.payout?.commission || 0, payoutCurrency);
    const opts = overrideOpts();
    if (opts === null) return;
    if (!window.confirm(`Send ${amount} to ${who} via Airwallex for ${MONTH_LABEL(period.year, period.month)}?\n\nThis transfers real funds to their linked beneficiary.`)) return;
    setBusy('pay');
    setMsg('');
    try {
      const r = await payCommission(rep.repUserId, period.year, period.month, opts);
      setMsg(r.simulated ? 'Simulated (dev) — recorded as paid.' : r.error ? `Failed: ${r.error}` : r.skipped ? `Skipped (${r.skipped}).` : `Sent ${fmtMoney(r.commission, r.currency)}.`);
      await onChanged();
    } catch (e) {
      setMsg(e.response?.data?.error?.message || 'Payout failed.');
    } finally {
      setBusy('');
    }
  };

  return (
    <>
    <tr className="border-b border-line last:border-0 align-top">
      <td className="px-4 py-3">
        <div className="font-medium text-text-primary">{rep.name || rep.email}</div>
        <div className="text-xs text-text-muted">{rep.email}{rep.isActive === false ? ' · inactive' : ''}</div>
        {linked ? (
          <span className="mt-1 inline-block rounded-full bg-success-light px-1.5 py-0.5 text-[10px] font-medium text-success">Airwallex linked</span>
        ) : (
          <span className="mt-1 inline-block text-[10px] text-text-muted">no Airwallex beneficiary</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <input
            type="number" min={0} max={100} step="0.5"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            disabled={rep.settled}
            className="w-16 rounded-md border border-line px-2 py-1 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 disabled:bg-surface-raised"
          />
          <span className="text-xs text-text-muted">%</span>
          {!rep.settled && String(rate) !== String(rep.rate ?? '') && (
            <button onClick={saveRate} disabled={busy === 'rate'} className="ml-1 rounded border border-line px-2 py-1 text-xs font-medium text-accent hover:bg-surface-raised">
              {busy === 'rate' ? '…' : 'Set'}
            </button>
          )}
        </div>
        <label className="mt-1.5 flex items-center gap-1 text-[11px] text-text-muted">
          Paid in
          <select
            value={payoutCurrency}
            onChange={(e) => changeCurrency(e.target.value)}
            disabled={rep.settled || busy === 'cur'}
            title={rep.settled ? 'This month is paid — change it for future months on a later month' : 'The currency this rep is paid in (all their unpaid months)'}
            className="rounded border border-line bg-surface px-1 py-0.5 text-[11px] text-text-primary disabled:opacity-60"
          >
            {[...new Set([payoutCurrency, ...CURRENCIES])].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
      </td>
      <td className="px-4 py-3 text-sm">
        {!(rep.payout?.base > 0) ? (
          <span className="text-text-muted">—</span>
        ) : (
          <>
            <div className="tabular-nums text-text-secondary">{fmtMoney(rep.payout.base, payoutCurrency)}</div>
            {rep.breakdown && rep.breakdown.length > 1 && (
              <div className="text-[10px] text-text-muted">{rep.breakdown.map((b) => fmtMoney(b.base, b.currency)).join(' · ')}</div>
            )}
            {(orders.length > 0 || clawbacks.length > 0) && (
              <button onClick={() => setOpen((v) => !v)} className="mt-1 text-[11px] font-medium text-accent hover:underline">
                {open ? 'Hide details' : `${orders.length} order${orders.length === 1 ? '' : 's'}${clawbacks.length ? ` · ${clawbacks.length} clawback${clawbacks.length > 1 ? 's' : ''}` : ''} ▾`}
              </button>
            )}
          </>
        )}
      </td>
      <td className="px-4 py-3 text-sm">
        {rep.rate == null ? (
          <span className="text-xs text-warning">set a rate</span>
        ) : !(rep.payout?.commission > 0) && !(clawback > 0) ? (
          <span className="text-text-muted">—</span>
        ) : (
          <>
            <div className="tabular-nums font-medium text-text-primary">{fmtMoney(rep.payout.commission || 0, payoutCurrency)}</div>
            {clawback > 0 && (
              <div className="text-[10px] text-text-muted" title="Commission earlier paid on money since refunded, or on orders since cancelled">
                {fmtMoney(rep.payout.gross || 0, payoutCurrency)} − {fmtMoney(clawback, payoutCurrency)} clawback
              </div>
            )}
            {!rep.settled && rep.payout?.pendingClawback > clawback && (
              <div className="text-[10px] text-warning">{fmtMoney(rep.payout.pendingClawback - clawback, payoutCurrency)} more carries to next month</div>
            )}
          </>
        )}
      </td>
      <td className="px-4 py-3">
        {rep.settled ? (
          <>
            <span className="rounded-full bg-success-light px-2 py-0.5 text-xs font-medium text-success">{paidViaAirwallex ? 'Paid · Airwallex' : 'Paid'}</span>
            {report.override && <div className="mt-1 text-[10px] text-warning" title={report.override.note || ''}>Paid without the rep’s approval</div>}
          </>
        ) : !(rep.payout?.base > 0) ? (
          <span className="text-xs text-text-muted">Nothing earned</span>
        ) : (
          <>
            <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STAGE[report.stage]?.cls || ''}`}>{STAGE[report.stage]?.label || report.stage}</span>
            <div className="mt-1 max-w-[220px] text-[10px] text-text-muted">
              {report.stage === 'APPROVED' && `Approved ${fmtWhen(report.approvedAt)}`}
              {report.stage === 'SENT' && `Report sent ${fmtWhen(report.sentAt)}`}
              {report.stage === 'CHANGED' && `Approved ${fmtMoney(report.approvedAmount, report.approvedCurrency)} — resend so they can approve the new figure`}
              {report.stage === 'QUERY' && <span className="text-text-secondary">“{report.query?.note}”</span>}
              {report.stage === 'NOT_SENT' && (ended ? 'Send the report so they can approve it' : 'Reports go out once the month ends')}
            </div>
          </>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {rep.settled ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button onClick={resendPaid} disabled={busy === 'paid'} title="Email the rep the paid confirmation + remittance PDF again" className="rounded-md border border-line px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50">
              {busy === 'paid' ? 'Sending…' : 'Resend paid email'}
            </button>
            {paidViaAirwallex ? (
              <span className="text-[11px] text-text-muted" title="The transfer has been sent — correct it as an adjustment on a later month">Sent — can’t undo</span>
            ) : (
              <button onClick={unsettle} disabled={busy === 'settle'} className="rounded-md border border-line px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised">
                {busy === 'settle' ? '…' : 'Undo'}
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Step 1: the report (the main action until the rep has approved). */}
            {ended && rep.rate != null && !approved && (
              <button
                onClick={sendReport}
                disabled={busy === 'report'}
                className={`rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${report.stage === 'NOT_SENT' || report.stage === 'CHANGED' ? 'bg-accent text-white hover:bg-accent-hover' : 'border border-line text-text-secondary hover:bg-surface-raised'}`}
              >
                {busy === 'report' ? 'Sending…' : report.sentCount > 0 ? 'Resend report' : 'Email report'}
              </button>
            )}
            {report.stage === 'QUERY' && (
              <a href={`mailto:${rep.email}?subject=${encodeURIComponent(`Your ${MONTH_LABEL(period.year, period.month)} commission`)}`} className="rounded-md border border-line px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised">
                Reply
              </a>
            )}
            {/* Step 2: pay — unlocked by the rep's approval; admins can override. */}
            {canSettle && (approved || isAdmin) && (
              <>
                {linked && (
                  <button onClick={payVia} disabled={busy === 'pay'} title={approved ? `Send ${fmtMoney(rep.payout?.commission || 0, payoutCurrency)} to the rep's linked Airwallex beneficiary (one transfer, in ${payoutCurrency})` : 'Admin: pay without the rep’s approval'} className={`rounded-md border px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${approved ? 'border-accent text-accent hover:bg-accent-light' : 'border-dashed border-line text-text-muted hover:bg-surface-raised'}`}>
                    {busy === 'pay' ? 'Sending…' : 'Pay via Airwallex'}
                  </button>
                )}
                <button onClick={settle} disabled={busy === 'settle'} title={approved ? 'Record as paid (money moved outside the platform)' : 'Admin: record as paid without the rep’s approval'} className={`rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${approved ? 'bg-accent text-white hover:bg-accent-hover' : 'border border-dashed border-line text-text-muted hover:bg-surface-raised'}`}>
                  {busy === 'settle' ? '…' : 'Mark paid'}
                </button>
              </>
            )}
            {canSettle && !approved && !isAdmin && (
              <span className="text-[11px] text-text-muted" title="An admin can pay without approval">Pay unlocks once {who} approves</span>
            )}
          </div>
        )}
        {msg && <div className="mt-1 text-[11px] text-text-muted">{msg}</div>}
      </td>
    </tr>
    {open && (orders.length > 0 || clawbacks.length > 0) && (
      <tr className="border-b border-line bg-surface-raised/30">
        <td colSpan={6} className="px-4 py-3">
          {clawbacks.length > 0 && (
            <div className="mb-3 rounded-md border border-warning/40 bg-warning-light/40 p-2">
              <div className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
                Clawbacks {rep.settled ? 'deducted from this payout' : 'to deduct when this month is settled'}
              </div>
              <ul className="mt-1 space-y-0.5 text-xs">
                {clawbacks.map((c) => (
                  <li key={c.id} className="flex justify-between gap-3">
                    <span className="text-text-secondary">
                      {c.note || (c.reason === 'CANCELLATION' ? 'Order cancelled' : 'Refund')} · from {MONTH_LABEL(c.source.year, c.source.month)}’s payout
                    </span>
                    <span className="tabular-nums font-medium text-text-primary">−{fmtMoney(c.amount, c.currency)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
            Orders paid in {MONTH_LABEL(period.year, period.month)} — goods received × {rep.rate != null ? `${rep.rate}%` : 'rate'}
          </div>
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-text-muted">
                <th className="py-1 pr-4 font-medium">Order</th>
                <th className="py-1 pr-4 font-medium">Client</th>
                <th className="py-1 pr-4 text-right font-medium">Goods received</th>
                <th className="py-1 text-right font-medium">Commission</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.orderId} className="border-t border-line/60">
                  <td className="py-1 pr-4 font-mono text-xs text-text-secondary">{o.ref}</td>
                  <td className="py-1 pr-4 text-text-primary">{o.company}</td>
                  <td className="py-1 pr-4 text-right tabular-nums text-text-secondary">{fmtMoney(o.goods, o.currency)}</td>
                  <td className="py-1 text-right tabular-nums font-medium text-text-primary">{o.commission != null ? fmtMoney(o.commission, o.currency) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 text-right text-xs text-text-muted">
            Total paid to {rep.name || rep.email}: <span className="font-medium text-text-primary">{fmtMoney(rep.payout?.commission || 0, payoutCurrency)}</span>
            {orders.some((o) => o.currency !== payoutCurrency) && ' (converted to their currency)'}
          </div>
        </td>
      </tr>
    )}
    </>
  );
}

function AdminCommissions() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = can(user, CAP.MANAGE_TEAM);
  // ?year=&month= (from the "rep approved" notification) opens that month.
  const [params] = useSearchParams();
  const [period, setPeriod] = useState(() => {
    const y = Number(params.get('year'));
    const m = Number(params.get('month'));
    return y && m >= 1 && m <= 12 ? { year: y, month: m } : prevMonth();
  });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  const doSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const r = await syncBeneficiaries();
      setSyncMsg(`${r.linked} of ${r.reps} reps linked to an Airwallex beneficiary${r.changed ? ` · ${r.changed} updated` : ''}.`);
      await load();
    } catch (e) {
      setSyncMsg(e.response?.data?.error?.message || 'Could not sync beneficiaries.');
    } finally {
      setSyncing(false);
    }
  };

  const load = () => {
    setLoading(true);
    return fetchCommissions(period.year, period.month)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.year, period.month]);

  const shiftMonth = (delta) =>
    setPeriod((p) => {
      const idx = p.year * 12 + (p.month - 1) + delta;
      return { year: Math.floor(idx / 12), month: (idx % 12) + 1 };
    });

  const now = new Date();
  const ended = monthIdx(period.year, period.month) < monthIdx(now.getUTCFullYear(), now.getUTCMonth() + 1);
  const unsent = (data?.reps || []).filter((r) => !r.settled && r.rate != null && r.payout?.base > 0 && r.report?.stage === 'NOT_SENT');
  const counts = (data?.reps || []).reduce((acc, r) => {
    if (!r.settled && r.payout?.base > 0) acc[r.report?.stage] = (acc[r.report?.stage] || 0) + 1;
    return acc;
  }, {});
  const sendAll = async () => {
    if (!window.confirm(`Email the ${MONTHS[period.month - 1]} ${period.year} commission report to ${unsent.length} rep${unsent.length === 1 ? '' : 's'} who haven't had it yet?\n\nAdmins get a copy of each.`)) return;
    setBulkBusy(true);
    setSyncMsg('');
    try {
      const r = await sendAllCommissionReports(period.year, period.month);
      setSyncMsg(`${r.sent} report${r.sent === 1 ? '' : 's'} sent${r.failed ? ` · ${r.failed} failed — try those individually` : ''}.`);
      await load();
    } catch (e) {
      setSyncMsg(errText(e, 'Could not send the reports.'));
    } finally {
      setBulkBusy(false);
    }
  };

  const unattributed = data?.unattributed?.filter((u) => u.base > 0) || [];
  const unattributedCompanies = data?.unattributedCompanies || [];
  const label = useMemo(() => `${MONTHS[period.month - 1]} ${period.year}`, [period]);

  return (
    <>
      <PageHeader
        title="Commissions"
        subtitle="Commission on the goods value of payments received in the month. Email each rep their report → they approve it → pay it (Airwallex or Mark paid)."
      />

      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => shiftMonth(-1)} className="rounded-md border border-line px-3 py-1.5 text-sm hover:bg-surface-raised">←</button>
        <span className="min-w-[120px] text-center text-sm font-semibold text-text-primary">{label}</span>
        <button onClick={() => shiftMonth(1)} className="rounded-md border border-line px-3 py-1.5 text-sm hover:bg-surface-raised">→</button>
        <button onClick={() => setPeriod(prevMonth())} className="text-xs text-accent hover:underline">Last month</button>
        {ended && unsent.length > 0 && (
          <button onClick={sendAll} disabled={bulkBusy} className="ml-auto rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50">
            {bulkBusy ? 'Sending…' : `Email all unsent reports (${unsent.length})`}
          </button>
        )}
        <button
          onClick={doSync}
          disabled={syncing}
          title="Match Airwallex beneficiaries (transfer contacts) to reps by email, so you can pay via Airwallex"
          className={`${ended && unsent.length > 0 ? '' : 'ml-auto '}rounded-md border border-line px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50`}
        >
          {syncing ? 'Syncing…' : 'Sync Airwallex beneficiaries'}
        </button>
      </div>
      {data?.reps?.length > 0 && Object.keys(counts).length > 0 && (
        <p className="mb-3 text-xs text-text-muted">
          {[
            counts.APPROVED && `${counts.APPROVED} approved — ready to pay`,
            counts.SENT && `${counts.SENT} waiting for the rep`,
            counts.QUERY && `${counts.QUERY} with a question`,
            counts.CHANGED && `${counts.CHANGED} changed since approval`,
            counts.NOT_SENT && (ended ? `${counts.NOT_SENT} report${counts.NOT_SENT === 1 ? '' : 's'} not sent` : 'Reports go out once the month ends'),
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      {syncMsg && <div className="mb-3 rounded-md bg-accent-light px-3 py-2 text-xs text-text-secondary">{syncMsg}</div>}

      {loading ? (
        <div className="text-sm text-text-muted">Loading…</div>
      ) : !data || data.reps.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">
          No reps with a commission rate or received payments in {label}.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface shadow-sm">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-surface-raised/60 text-left text-xs text-text-muted">
                <th className="px-4 py-2 font-medium">Rep</th>
                <th className="px-4 py-2 font-medium">Rate</th>
                <th className="px-4 py-2 font-medium">Goods received</th>
                <th className="px-4 py-2 font-medium">Commission</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.reps.map((rep) => (
                <RepRow key={rep.repUserId} rep={rep} period={period} onChanged={load} isAdmin={isAdmin} ended={ended} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {unattributed.length > 0 && (
        <div className="mt-3 rounded-md border border-line bg-surface p-3">
          <p className="text-xs text-text-secondary">
            <span className="font-medium text-text-primary">{unattributed.map((u) => fmtMoney(u.base, u.currency)).join(' · ')}</span>{' '}
            of goods payments earned no commission because the account has no primary rep. Set one on the client to
            include it from next month.
          </p>
          {/* Naming the accounts — the old note told you to "assign a rep to those companies" without
              saying which, so it was impossible to act on. */}
          {unattributedCompanies.length > 0 && (
            <ul className="mt-2 space-y-1">
              {unattributedCompanies.map((c) => (
                <li key={c.companyId || c.company} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                  {c.companyId ? (
                    <Link to={`/clients/${c.companyId}`} className="font-medium text-accent hover:underline">
                      {c.company}
                    </Link>
                  ) : (
                    <span className="font-medium text-text-primary">{c.company}</span>
                  )}
                  <span className="text-text-muted">{c.lines.map((l) => fmtMoney(l.base, l.currency)).join(' · ')}</span>
                  {c.reason === 'rep_not_a_rep_account' && (
                    <span className="text-warning">— its primary rep is no longer a rep account</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

// A rep's own view of one month: the consolidated goods base → commission in their currency, with a
// per-currency breakdown when their clients paid in more than one currency.
function CommissionLines({ entry, rate }) {
  if (!entry || (!(entry.base > 0) && !(entry.commission) && !(entry.clawback > 0))) return <span className="text-sm text-text-muted">No payments received.</span>;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-4 text-sm">
        <span className="text-text-secondary">
          {fmtMoney(entry.base, entry.currency)} goods{rate != null ? ` × ${rate}%` : ''}
        </span>
        <span className="font-medium tabular-nums text-text-primary">
          {entry.commission != null ? fmtMoney(entry.commission, entry.currency) : '—'}
        </span>
      </div>
      {entry.breakdown && entry.breakdown.length > 1 && (
        <div className="text-[11px] text-text-muted">from {entry.breakdown.map((b) => fmtMoney(b.base, b.currency)).join(' · ')}</div>
      )}
      {entry.clawback > 0 && (
        <div className="text-[11px] text-text-muted">after a {fmtMoney(entry.clawback, entry.currency)} clawback (refunds / cancellations on earlier paid months)</div>
      )}
    </div>
  );
}

// One report the TEAMM8 team sent, waiting on the rep: the figure, how it's made up, and the two
// choices. The approval is for this exact amount — if it changes, the team resends and they approve again.
function ReportToApprove({ r, highlight, openQuery, onDone }) {
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null); // { ok, text }
  const [asking, setAsking] = useState(openQuery);
  const [note, setNote] = useState('');
  const [showOrders, setShowOrders] = useState(false);
  const p = r.payout || {};
  const cur = p.currency;
  const stage = r.report?.stage;
  const label = MONTH_LABEL(r.year, r.month);

  const approve = async () => {
    if (!window.confirm(`Approve ${fmtMoney(p.commission, cur)} for ${label}?`)) return;
    setBusy('approve');
    setMsg(null);
    try {
      await approveMyCommission(r.year, r.month, p.commission, cur);
      setMsg({ ok: true, text: 'Approved — thanks. We’ll pay it shortly and email you when it’s sent.' });
      await onDone();
    } catch (e) {
      setMsg({ ok: false, text: errText(e, 'Could not approve — please try again.') });
      if (e.response?.status === 409) await onDone();
    } finally {
      setBusy('');
    }
  };
  const ask = async () => {
    setBusy('query');
    setMsg(null);
    try {
      await queryMyCommission(r.year, r.month, note.trim());
      setAsking(false);
      setNote('');
      setMsg({ ok: true, text: 'Sent to the TEAMM8 team — they’ll get back to you.' });
      await onDone();
    } catch (e) {
      setMsg({ ok: false, text: errText(e, 'Could not send — please try again.') });
    } finally {
      setBusy('');
    }
  };

  return (
    <div id={`review-${r.year}-${String(r.month).padStart(2, '0')}`} className={`rounded-lg border bg-surface p-5 shadow-sm ${highlight ? 'border-accent ring-2 ring-accent/30' : 'border-line'}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
            {label} · {stage === 'APPROVED' ? 'you approved this' : stage === 'QUERY' ? 'question sent to the team' : 'waiting for your approval'}
          </div>
          <div className="mt-0.5 text-2xl font-bold tabular-nums text-text-primary">{fmtMoney(p.commission, cur)}</div>
          <div className="text-xs text-text-muted">
            {fmtMoney(p.base, cur)} received × {r.rate}%{p.clawback > 0 ? ` = ${fmtMoney(p.gross, cur)} − ${fmtMoney(p.clawback, cur)} clawback` : ''} · paid in {cur}
          </div>
        </div>
        {stage === 'APPROVED' ? (
          <span className="rounded-full bg-success-light px-3 py-1 text-xs font-medium text-success">Approved {fmtWhen(r.report.approvedAt)} · we’ll pay it shortly</span>
        ) : (
          <div className="flex flex-wrap gap-2">
            <button onClick={approve} disabled={busy === 'approve'} className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50">
              {busy === 'approve' ? 'Approving…' : `Approve ${fmtMoney(p.commission, cur)}`}
            </button>
            <button onClick={() => setAsking((v) => !v)} className="rounded-md border border-line px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-raised">
              Something’s not right
            </button>
          </div>
        )}
      </div>
      {stage === 'QUERY' && r.report?.query?.note && <p className="mt-2 text-xs text-text-secondary">You said: “{r.report.query.note}”</p>}
      {asking && (
        <div className="mt-3 rounded-md border border-line bg-surface-raised/40 p-3">
          <label className="text-xs font-medium text-text-secondary" htmlFor={`q-${r.year}-${r.month}`}>
            Tell us what looks wrong (e.g. an order that’s missing)
          </label>
          <textarea id={`q-${r.year}-${r.month}`} value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1 w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30" />
          <div className="mt-2 flex gap-2">
            <button onClick={ask} disabled={busy === 'query' || note.trim().length < 3} className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50">
              {busy === 'query' ? 'Sending…' : 'Send to the TEAMM8 team'}
            </button>
            <button onClick={() => setAsking(false)} className="text-xs text-text-muted hover:text-text-primary">Cancel</button>
          </div>
        </div>
      )}
      {msg && <p className={`mt-2 text-xs ${msg.ok ? 'text-success' : 'text-error'}`}>{msg.text}</p>}
      {(r.orders?.length > 0 || r.clawbacks?.length > 0) && (
        <button onClick={() => setShowOrders((v) => !v)} className="mt-3 text-xs font-medium text-accent hover:underline">
          {showOrders ? 'Hide the orders' : `See the ${r.orders?.length || 0} order${r.orders?.length === 1 ? '' : 's'} behind this ▾`}
        </button>
      )}
      {showOrders && (
        <div className="mt-2 space-y-1 text-sm">
          {(r.orders || []).map((o) => (
            <div key={o.orderId} className="flex justify-between gap-3 border-t border-line/60 pt-1">
              <span className="min-w-0 truncate text-text-secondary">
                {o.company} · <span className="font-mono text-xs">{o.ref}</span>
              </span>
              <span className="shrink-0 tabular-nums text-text-primary">
                {fmtMoney(o.goods, o.currency)} → {o.commission != null ? fmtMoney(o.commission, o.currency) : '—'}
              </span>
            </div>
          ))}
          {(r.clawbacks || []).map((c) => (
            <div key={c.id} className="flex justify-between gap-3 border-t border-line/60 pt-1 text-xs">
              <span className="text-text-secondary">{c.note || 'Clawback'}</span>
              <span className="tabular-nums text-error">−{fmtMoney(c.amount, c.currency)}</span>
            </div>
          ))}
          {(r.breakdown || []).some((b) => b.currency !== cur) && <p className="pt-1 text-[11px] text-text-muted">Amounts in other currencies are converted to {cur} at the day’s rate.</p>}
        </div>
      )}
    </div>
  );
}

// A rep's own commissions. `embedded` skips the page header so it can sit inside the rep home.
export function RepCommissions({ embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // ?review=2026-08 (the link in the report email) highlights that month; #query opens the question box.
  const [params] = useSearchParams();
  const review = params.get('review');
  const openQuery = typeof window !== 'undefined' && window.location.hash === '#query';
  const load = () =>
    fetchMyCommissions()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (review && data?.reports?.length) document.getElementById(`review-${review}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [review, data]);
  const reports = data?.reports || [];

  return (
    <>
      {embedded ? (
        <>
          <h2 className="text-lg font-semibold text-text-primary">My commissions</h2>
          <p className="mb-3 mt-0.5 text-xs text-text-muted">
            Earned on the goods value (not shipping) when your clients pay; paid early the following month. Refunds and cancellations after a payout are deducted from your next one.
          </p>
        </>
      ) : (
        <PageHeader
          title="My commissions"
          subtitle="Your commission on the goods value of payments your clients pay each month, settled at the start of the following month."
        />
      )}
      {loading ? (
        <div className="text-sm text-text-muted">Loading…</div>
      ) : !data?.isRep ? (
        <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">
          Commissions apply to sales reps only.
        </div>
      ) : (
        <>
        {reports.length > 0 && (
          <div className="mb-4 space-y-3">
            {reports.map((r) => {
              const key = `${r.year}-${String(r.month).padStart(2, '0')}`;
              return <ReportToApprove key={key} r={r} highlight={review === key} openQuery={review === key && openQuery} onDone={load} />;
            })}
          </div>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-text-primary">This month so far · {MONTH_LABEL(data.current.year, data.current.month)}</h2>
              <span className="rounded-full bg-accent-light px-2 py-0.5 text-xs text-text-secondary">
                {data.rate != null ? `${data.rate}%` : 'no rate set'}
              </span>
            </div>
            <p className="mb-3 mt-0.5 text-xs text-text-muted">Accrues as your clients' payments arrive; paid after month-end{data.currency ? `, in ${data.currency}` : ''}.</p>
            <CommissionLines entry={data.current} rate={data.rate} />
            {data.rate == null && (
              <p className="mt-3 text-xs text-warning">An admin hasn't set your commission rate yet — amounts will show once they do.</p>
            )}
            {data.pendingDeductions?.total > 0 && (
              <div className="mt-3 rounded-md border border-warning/40 bg-warning-light/40 p-2 text-xs">
                <div className="font-medium text-text-primary">
                  {fmtMoney(data.pendingDeductions.total, data.currency)} will come off your next payout
                </div>
                <p className="mt-0.5 text-text-muted">Commission you were already paid on money that was later refunded, or on orders later cancelled:</p>
                <ul className="mt-1 space-y-0.5">
                  {data.pendingDeductions.items.map((c) => (
                    <li key={c.id} className="flex justify-between gap-3">
                      <span className="text-text-secondary">{c.note}</span>
                      <span className="tabular-nums">−{fmtMoney(c.amount, c.currency)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-text-primary">History</h2>
            {data.history.length === 0 ? (
              <p className="text-sm text-text-muted">No past months yet.</p>
            ) : (
              <div className="space-y-3">
                {data.history.map((h) => (
                  <div key={`${h.year}-${h.month}`} className="border-b border-line pb-3 last:border-0 last:pb-0">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-sm font-medium text-text-primary">{MONTH_LABEL(h.year, h.month)}</span>
                      {h.status === 'paid' ? (
                        <span className="rounded-full bg-success-light px-2 py-0.5 text-xs font-medium text-success">
                          Paid{h.paidAt ? ` · ${new Date(h.paidAt).toLocaleDateString()}` : ''}
                        </span>
                      ) : (
                        <span className="rounded-full bg-surface-raised px-2 py-0.5 text-xs text-text-muted">Not paid yet</span>
                      )}
                    </div>
                    <CommissionLines entry={h} rate={h.status === 'paid' ? null : data.rate} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </>
      )}
    </>
  );
}

export default function Commissions() {
  const user = useAuthStore((s) => s.user);
  return can(user, CAP.MANAGE_PAYMENTS) ? <AdminCommissions /> : <RepCommissions />;
}
